/**
 * ErrorBoundary — catch React rendering errors with a friendly fallback.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { Box, Typography, Button } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { withTranslation, type WithTranslation } from "react-i18next";

interface Props extends WithTranslation {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "40vh",
            gap: 2,
            textAlign: "center",
            p: 3,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 64, color: "error.main" }} />
          <Typography variant="h5">{this.props.t("errorBoundary.title")}</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 400 }}>
            {this.state.error?.message ?? this.props.t("errorBoundary.message")}
          </Typography>
          <Button variant="contained" onClick={this.handleReset}>
            {this.props.t("errorBoundary.retry")}
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default withTranslation("common")(ErrorBoundary);
